import React, { useState, useEffect, useCallback, useRef } from "react";
import S from "../styles";

const API_URL = process.env.REACT_APP_API_URL || "https://api.nycmanufacturing.com";
const MONO = "'JetBrains Mono', monospace";
const MUTED = "rgba(10,10,15,0.58)";

// Shopify gives ISO strings (cursor, connect time); token expiry is epoch seconds.
const fmtIso = (s) => (s ? new Date(s).toLocaleString() : "—");
const fmtTs = (ts) => {
  const n = Number(ts);
  return n ? new Date(n * 1000).toLocaleString() : "—";
};

const cardTitle = { fontSize: 14, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1.2, color: MUTED, marginBottom: 12 };
const help = { fontSize: 13, color: MUTED, lineHeight: 1.6 };
const codeBox = { marginTop: 8, padding: "10px 14px", background: "#FFFFFF", border: "2px solid #0A0A0F", fontFamily: MONO, fontSize: 13, color: "#0033FF", wordBreak: "break-all" };

export default function ShopifyPage({ store }) {
  const storeKey = store === "vibratovault" ? "vibratovault" : "designshop";
  const storeLabel = storeKey === "designshop" ? "Design Shop" : "Vibrato Vault";
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("orders");
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [settings, setSettings] = useState({
    connected: false, appConfigured: false, shopDomain: "", shopName: "",
    lastSynced: "", connectedAt: "", tokenExpiresAt: "", refreshExpiresAt: "", scopes: "", apiVersion: "",
  });
  const [shopInput, setShopInput] = useState("");
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");
  const [search, setSearch] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [connecting, setConnecting] = useState(false);
  // CEO-only app credential form
  const [cred, setCred] = useState({ empId: "", password: "", clientId: "", clientSecret: "" });
  const [credSaving, setCredSaving] = useState(false);
  const [showCredForm, setShowCredForm] = useState(false);
  const connectPoll = useRef(null);

  const fetchSettings = useCallback(() => {
    fetch(`${API_URL}/api/shopify/settings?store=${storeKey}`)
      .then((r) => r.json())
      .then((s) => {
        setSettings((prev) => ({ ...prev, ...s }));
        if (s.shopDomain) setShopInput((cur) => cur || s.shopDomain);
      })
      .catch(() => {});
  }, [storeKey]);

  const fetchOrders = useCallback(() => {
    setLoading(true);
    setError("");
    fetch(`${API_URL}/api/shopify/orders?store=${storeKey}`)
      .then((r) => r.json())
      .then((data) => {
        if (data.error) setError(data.error);
        else if (data.orders) setOrders(data.orders);
        setLoading(false);
      })
      .catch((err) => { setError(`Fetch failed: ${err.message}`); setLoading(false); });
  }, [storeKey]);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);
  useEffect(() => { if (tab === "orders") fetchOrders(); }, [tab, fetchOrders]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(""), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  // Surface the OAuth callback result if the user landed back here with a param
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("shopify_connected")) {
      setToast("Connected to Shopify ✓");
      fetchSettings();
    } else if (params.get("shopify_error")) {
      setError(`Shopify connection failed: ${params.get("shopify_error")}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fallback auto-sync while the page is open (server poller also runs every 5 min)
  useEffect(() => {
    if (!settings.connected) return undefined;
    const iv = setInterval(() => {
      fetch(`${API_URL}/api/shopify/sync?store=${storeKey}`, { method: "POST" })
        .then((r) => r.json())
        .then((d) => { if (d && d.synced > 0) { fetchOrders(); fetchSettings(); } })
        .catch(() => {});
    }, 60000);
    return () => clearInterval(iv);
  }, [settings.connected, storeKey, fetchOrders, fetchSettings]);

  useEffect(() => () => { if (connectPoll.current) clearInterval(connectPoll.current); }, []);

  const connect = async () => {
    setError("");
    try {
      const r = await fetch(`${API_URL}/api/shopify/oauth/start?store=${storeKey}&shop=${encodeURIComponent(shopInput.trim())}`);
      const data = await r.json();
      if (data.error) { setError(data.error); return; }
      window.open(data.authorizeUrl, "_blank", "noopener");
      // Poll for the connection to complete (callback runs in the new tab)
      setConnecting(true);
      if (connectPoll.current) clearInterval(connectPoll.current);
      let tries = 0;
      connectPoll.current = setInterval(async () => {
        tries += 1;
        try {
          const s = await fetch(`${API_URL}/api/shopify/settings?store=${storeKey}`).then((x) => x.json());
          if (s.connected) {
            setSettings((prev) => ({ ...prev, ...s }));
            setConnecting(false);
            setToast("Connected to Shopify ✓");
            clearInterval(connectPoll.current);
          } else if (tries > 75) { // ~2.5 min
            setConnecting(false);
            clearInterval(connectPoll.current);
          }
        } catch (e) { /* ignore transient errors */ }
      }, 2000);
    } catch (e) { setError(`Could not start Shopify connect: ${e.message}`); }
  };

  const disconnect = async () => {
    if (!window.confirm(`Disconnect ${storeLabel} from Shopify? Inventory stops auto-syncing until you connect again.`)) return;
    await fetch(`${API_URL}/api/shopify/disconnect?store=${storeKey}`, { method: "POST" }).catch(() => {});
    fetchSettings();
    setToast("Disconnected from Shopify");
  };

  const syncNow = async () => {
    setSyncing(true);
    setError("");
    try {
      const r = await fetch(`${API_URL}/api/shopify/sync?store=${storeKey}`, { method: "POST" });
      const d = await r.json();
      if (d.error) setError(d.error);
      else {
        const errs = (d.errors || []).length;
        const extra = [
          d.testSkipped ? `${d.testSkipped} test` : "",
          d.cancelled ? `${d.cancelled} cancelled` : "",
          errs ? `${errs} error${errs > 1 ? "s" : ""}` : "",
        ].filter(Boolean).join(" • ");
        setToast(`Synced ${d.synced} • skipped ${d.skipped}${extra ? ` • ${extra}` : ""}`);
        fetchOrders();
        fetchSettings();
      }
    } catch (e) { setError(`Sync failed: ${e.message}`); }
    setSyncing(false);
  };

  const saveCredentials = async () => {
    setCredSaving(true);
    setError("");
    try {
      const r = await fetch(`${API_URL}/api/shopify/app-credentials`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(cred),
      });
      const d = await r.json();
      if (d.error) setError(d.error);
      else {
        setToast("Shopify app credentials saved ✓");
        setShowCredForm(false);
        setCred({ empId: "", password: "", clientId: "", clientSecret: "" });
        fetchSettings();
      }
    } catch (e) { setError(`Save failed: ${e.message}`); }
    setCredSaving(false);
  };

  const filteredOrders = orders.filter((o) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (o.title || "").toLowerCase().includes(s)
      || (o.sku || "").toLowerCase().includes(s)
      || (o.orderName || "").toLowerCase().includes(s)
      || (o.items || []).some((i) => (i.sku || "").toLowerCase().includes(s));
  });

  const tabs = [
    { key: "orders", label: "Orders" },
    { key: "connect", label: "Connection" },
  ];

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, gap: 12, flexWrap: "wrap" }}>
        <div>
          <h2 style={{ fontFamily: "'Archivo Black', 'Archivo', sans-serif", fontSize: 32, fontWeight: 900, color: "#0A0A0F", letterSpacing: "-0.02em", textTransform: "uppercase", lineHeight: 1 }}>Shopify Integration</h2>
          <div style={{ fontSize: 13, color: MUTED, marginTop: 2 }}>
            direct Shopify API · {storeLabel}{settings.shopName ? ` · ${settings.shopName}` : ""}
          </div>
        </div>
        {settings.connected && (
          <button onClick={syncNow} disabled={syncing} style={{ ...S.addBtn, opacity: syncing ? 0.6 : 1, cursor: syncing ? "wait" : "pointer" }}>
            {syncing ? "Syncing…" : "Sync Now"}
          </button>
        )}
      </div>

      <div style={{ ...S.nav, marginBottom: 20 }}>
        {tabs.map((t) => (
          <button key={t.key} onClick={() => { setTab(t.key); setSearch(""); }} style={{ ...S.navBtn, ...(tab === t.key ? S.navBtnActive : {}) }}>
            {t.label}
          </button>
        ))}
      </div>

      {toast && (
        <div style={{ marginBottom: 16, padding: "10px 14px", background: "rgba(52,211,153,0.12)", border: "1px solid #34d399", fontSize: 14, color: "#0c3a2a", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>{toast}</span>
          <button onClick={() => setToast("")} style={{ background: "none", border: "none", color: "#0c3a2a", cursor: "pointer", fontSize: 16 }}>x</button>
        </div>
      )}

      {error && (
        <div style={{ marginBottom: 16, padding: "10px 14px", background: "rgba(239,68,68,0.1)", border: "1px solid #ef4444", fontSize: 14, color: "#ef4444", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>{error}</span>
          <button onClick={() => setError("")} style={{ background: "none", border: "none", color: "#ef4444", cursor: "pointer", fontSize: 16 }}>x</button>
        </div>
      )}

      {/* Connection Tab */}
      {tab === "connect" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div style={{ ...S.lineCard, padding: 20 }}>
            <div style={cardTitle}>Store Connection</div>
            {settings.connected ? (
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                  <span style={{ width: 10, height: 10, background: "#34d399", display: "inline-block" }} />
                  <span style={{ fontSize: 16, color: "#0A0A0F", fontWeight: 600 }}>
                    Connected{settings.shopName ? ` — ${settings.shopName}` : ""}
                  </span>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "6px 16px", fontSize: 13, color: MUTED, marginBottom: 16 }}>
                  <span>Store address</span><span style={{ fontFamily: MONO, color: "#0A0A0F", wordBreak: "break-all" }}>{settings.shopDomain}</span>
                  <span>Counting orders from</span><span style={{ fontFamily: MONO, color: "#0A0A0F" }}>{fmtIso(settings.connectedAt)}</span>
                  <span>Last order change seen</span><span style={{ fontFamily: MONO, color: "#0A0A0F" }}>{fmtIso(settings.lastSynced)}</span>
                  <span>Access renews by</span><span style={{ fontFamily: MONO, color: "#0A0A0F" }}>{fmtTs(settings.refreshExpiresAt)}</span>
                  <span>Scopes</span><span style={{ fontFamily: MONO, color: "#0A0A0F" }}>{settings.scopes}</span>
                </div>
                <p style={{ ...help, marginBottom: 16 }}>
                  The server checks Shopify for new paid orders every 5 minutes. It takes each sold SKU off {storeLabel} inventory one time. Test orders and cancelled orders do not change stock.
                </p>
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={syncNow} disabled={syncing} style={{ ...S.addBtn, opacity: syncing ? 0.6 : 1 }}>{syncing ? "Syncing…" : "Sync Now"}</button>
                  <button onClick={disconnect} style={{ ...S.clearBtn, fontSize: 13 }}>Disconnect</button>
                </div>
              </div>
            ) : (
              <div style={{ maxWidth: 460 }}>
                <p style={{ ...help, fontSize: 14, marginBottom: 12 }}>
                  {settings.appConfigured
                    ? `Type the store's Shopify address, then approve access in Shopify. Only orders placed after you connect come off inventory.`
                    : "Add the Shopify app credentials below first (CEO only). Then connect the store."}
                </p>
                <input value={shopInput} onChange={(e) => setShopInput(e.target.value)} placeholder="wallplate-studio.myshopify.com" style={{ ...S.input, width: "100%", marginBottom: 6 }} />
                <div style={{ fontSize: 12, color: MUTED, marginBottom: 12 }}>
                  The .myshopify.com address, not the public website address. You can also paste the Shopify admin URL.
                </div>
                <button onClick={connect} disabled={!settings.appConfigured || connecting || !shopInput.trim()} style={{ ...S.addBtn, opacity: (!settings.appConfigured || connecting || !shopInput.trim()) ? 0.5 : 1, cursor: (!settings.appConfigured || connecting) ? "not-allowed" : "pointer" }}>
                  {connecting ? "Waiting for Shopify…" : `Connect ${storeLabel} to Shopify`}
                </button>
                {connecting && (
                  <div style={{ fontSize: 12, color: MUTED, marginTop: 8 }}>Approve access in the Shopify tab that opened, then come back here.</div>
                )}
              </div>
            )}
          </div>

          {/* App credentials (CEO only) */}
          <div style={{ ...S.lineCard, padding: 20 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <div style={{ ...cardTitle, marginBottom: 0 }}>Shopify App Credentials</div>
              {settings.appConfigured && !showCredForm && (
                <span style={{ fontSize: 13, color: "#34d399", display: "flex", alignItems: "center", gap: 6 }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#34d399" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>
                  Configured
                </span>
              )}
            </div>
            <p style={{ ...help, marginBottom: 16 }}>
              The Client ID and Client secret from the app's Settings page in the Shopify Dev Dashboard. The server keeps them and never shows them again. CEO login is required to save.
            </p>
            {(!settings.appConfigured || showCredForm) ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 10, maxWidth: 460 }}>
                <input value={cred.clientId} onChange={(e) => setCred({ ...cred, clientId: e.target.value })} placeholder="Client ID" style={S.input} />
                <input value={cred.clientSecret} onChange={(e) => setCred({ ...cred, clientSecret: e.target.value })} placeholder="Client secret" type="password" style={S.input} />
                <div style={{ display: "flex", gap: 10 }}>
                  <input value={cred.empId} onChange={(e) => setCred({ ...cred, empId: e.target.value })} placeholder="CEO Employee ID" style={{ ...S.input, flex: 1, minWidth: 0 }} />
                  <input value={cred.password} onChange={(e) => setCred({ ...cred, password: e.target.value })} placeholder="CEO Password" type="password" style={{ ...S.input, flex: 1, minWidth: 0 }} />
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={saveCredentials} disabled={credSaving} style={{ ...S.addBtn, opacity: credSaving ? 0.6 : 1 }}>{credSaving ? "Saving…" : "Save Credentials"}</button>
                  {settings.appConfigured && <button onClick={() => setShowCredForm(false)} style={{ ...S.clearBtn, fontSize: 13 }}>Cancel</button>}
                </div>
              </div>
            ) : (
              <button onClick={() => setShowCredForm(true)} style={{ ...S.clearBtn, fontSize: 13 }}>Update Credentials</button>
            )}
          </div>

          {/* One-time app setup */}
          <div style={{ ...S.lineCard, padding: 20 }}>
            <div style={cardTitle}>Shopify App Setup (one time)</div>
            <ol style={{ ...help, paddingLeft: 18, margin: 0 }}>
              <li>Open dev.shopify.com and create an app. Use the same Shopify login that owns the store.</li>
              <li>Make a new version. Set the scopes to <code style={{ fontFamily: MONO, color: "#0A0A0F" }}>read_orders</code> and <code style={{ fontFamily: MONO, color: "#0A0A0F" }}>read_products</code>. Add this exact redirect URL:</li>
            </ol>
            <div style={codeBox}>{API_URL.replace(/\/$/, "")}/api/shopify/oauth/callback</div>
            <ol start={3} style={{ ...help, paddingLeft: 18, margin: "8px 0 0" }}>
              <li>Release the version. Copy the Client ID and Client secret into the card above.</li>
              <li>Type the store address in Store Connection and select Connect.</li>
            </ol>
          </div>
        </div>
      )}

      {/* Orders Tab */}
      {tab === "orders" && (
        <>
          <div style={{ display: "flex", gap: 10, marginBottom: 16, alignItems: "center" }}>
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search orders..." style={{ ...S.input, flex: 1, maxWidth: 300, minWidth: 0 }} />
            <div style={{ fontSize: 14, color: MUTED }}>{filteredOrders.length} orders</div>
            <button onClick={fetchOrders} disabled={loading} style={{ ...S.iconBtn, opacity: loading ? 0.5 : 1 }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" />
                <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" />
              </svg>
            </button>
          </div>

          {!settings.connected && orders.length === 0 && (
            <div style={{ ...S.lineCard, padding: 24, textAlign: "center", marginBottom: 16 }}>
              <div style={{ fontSize: 16, color: MUTED, marginBottom: 8 }}>Not connected to Shopify yet</div>
              <button onClick={() => setTab("connect")} style={S.addBtn}>Connect to Shopify</button>
            </div>
          )}

          {loading ? (
            <div style={{ textAlign: "center", padding: 40, color: MUTED }}>Loading orders...</div>
          ) : (
            <div style={S.tableWrap}>
              <table style={S.table}>
                <thead>
                  <tr>
                    <th style={S.th}>Order #</th>
                    <th style={S.th}>Item</th>
                    <th style={S.th}>SKU</th>
                    <th style={{ ...S.th, textAlign: "right" }}>Amount</th>
                    <th style={{ ...S.th, textAlign: "center" }}>Qty Deducted</th>
                    <th style={S.th}>Ordered</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrders.map((o) => {
                    const items = o.items || [];
                    const hasMultiple = items.length > 1;
                    const isExpanded = expandedOrder === o.id;
                    return (
                      <React.Fragment key={o.id}>
                        <tr onClick={() => hasMultiple && setExpandedOrder(isExpanded ? null : o.id)} style={{ cursor: hasMultiple ? "pointer" : "default" }}>
                          <td style={{ ...S.td, fontFamily: MONO, fontSize: 13 }}>
                            {hasMultiple && (
                              <span style={{ display: "inline-block", width: 16, color: MUTED, fontSize: 12 }}>{isExpanded ? "▾" : "▸"}</span>
                            )}
                            {o.orderName}
                          </td>
                          <td style={{ ...S.td, maxWidth: 220 }}>
                            <div style={{ fontSize: 13, color: "#0A0A0F", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{o.title || "—"}</div>
                          </td>
                          <td style={{ ...S.td, fontFamily: MONO, fontSize: 12, color: o.sku ? "#0A0A0F" : MUTED }}>{o.sku || "—"}</td>
                          <td style={{ ...S.td, textAlign: "right", fontFamily: MONO, color: "#0033FF" }}>
                            {o.amount ? `$${Number(o.amount).toFixed(2)}` : "—"}
                          </td>
                          <td style={{ ...S.td, textAlign: "center", fontFamily: MONO, color: o.quantityDeducted > 0 ? "#34d399" : MUTED }}>
                            {o.quantityDeducted > 0 ? `-${o.quantityDeducted}` : "0"}
                          </td>
                          <td style={{ ...S.td, fontSize: 13, color: MUTED }}>{fmtIso(o.orderCreatedAt || o.syncedAt)}</td>
                        </tr>
                        {isExpanded && items.map((item, i) => (
                          <tr key={`${o.id}-${i}`} style={{ background: "#FFFFFF" }}>
                            <td style={{ ...S.td, paddingLeft: 32 }} />
                            <td style={{ ...S.td, maxWidth: 220 }}>
                              <div style={{ fontSize: 12, color: MUTED, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{item.title || "—"}</div>
                            </td>
                            <td style={{ ...S.td, fontFamily: MONO, fontSize: 11, color: MUTED }}>{item.sku || "—"}</td>
                            <td style={S.td} />
                            <td style={{ ...S.td, textAlign: "center", fontFamily: MONO, fontSize: 12, color: item.deducted > 0 ? "#34d399" : MUTED }}>
                              {item.quantity || 1} qty{item.deducted > 0 ? ` (−${item.deducted})` : ""}
                            </td>
                            <td style={S.td} />
                          </tr>
                        ))}
                      </React.Fragment>
                    );
                  })}
                  {filteredOrders.length === 0 && !loading && (
                    <tr><td colSpan={6} style={{ ...S.td, textAlign: "center", color: MUTED, padding: 24 }}>
                      {orders.length === 0 ? "No orders yet. Connect to Shopify and paid orders sync here by themselves." : "No matching orders"}
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
